import { AsyncPipe, formatDate } from '@angular/common';
import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnInit, Output } from '@angular/core';
import { MatLegacyCardModule } from '@angular/material/legacy-card';
import { GridModule } from '@progress/kendo-angular-grid';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { FacilityBillingRateModel } from 'src/app/facility-contracts/facilities/models/facility-billing-rate-model';
import { FacilityBillingRules } from 'src/app/facility-contracts/facilities/models/facility-billing-rules.model';
import { FacilityBillingShiftDifferential } from 'src/app/facility-contracts/facilities/models/facility-billing-shift-differential.model';
import { FacilityHolidayRule } from 'src/app/facility-contracts/facilities/models/facility-holiday-rule.model';
import { FacilityHolidayRules } from 'src/app/facility-contracts/facilities/models/facility-holiday-rules.model';
import { FacilityBillingService } from 'src/app/facility-contracts/facility-billing.service';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { IsEmptyPipe } from 'src/app/shared/pipes/is-empty.pipe';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { BillingRateGridItem } from 'src/app/travel-contracts/travel-contracts-grid/details/facility-billing-details/billing-info/billing-rate-grid-item';
import { HolidayRuleGridItem } from 'src/app/travel-contracts/travel-contracts-grid/details/facility-billing-details/billing-info/holiday-rule-glid-item';

@Component({
    selector: 'nova-billing-info',
    templateUrl: './billing-info.component.html',
    styleUrls: ['./billing-info.component.scss'],
    standalone: true,
    imports: [AsyncPipe, MatLegacyCardModule, IsEmptyPipe, GridModule]
})
export class BillingInfoComponent implements OnInit {
    readonly featureFlag = FeatureFlag;

    @Input()
    facilityId: number;

    @Output()
    loadingEvent = new EventEmitter<boolean>();

    holidayRulesCount = 0;
    holidayType = '';
    billingRules$: Observable<FacilityBillingRules>;
    holidayRules$: Observable<FacilityHolidayRules>;
    billingRates$: Observable<FacilityBillingRateModel[]>;
    localHolidayRules: HolidayRuleGridItem[] = [];

    constructor(
        private readonly _billingService: FacilityBillingService,
        private readonly _toasterService: ToasterService,
        @Inject(LOCALE_ID) private readonly _locale: string
    ) {}

    ngOnInit(): void {
        this.billingRules$ = this._billingService.getRulesByFacilityId(this.facilityId).pipe(
            catchError(() => {
                this._toasterService.toast('An error occurred while getting billing rules.');
                return of({} as FacilityBillingRules);
            })
        );

        this.holidayRules$ = this._billingService.getHolidayRulesByFacilityId(this.facilityId).pipe(
            tap((holidayRules) => this.handleHolidayRules(holidayRules)),
            tap(() => this.showLoading(false)),
            catchError(() => {
                this._toasterService.toast('An error occurred while getting holiday rules.');
                return of({} as FacilityHolidayRules);
            })
        );

        this.billingRates$ = this._billingService.getBillingRatesByFacilityId(this.facilityId).pipe(
            map((rates) => rates.map((rate) => new FacilityBillingRateModel(rate))),
            catchError(() => {
                this._toasterService.toast('An error occurred while getting billing rates.');
                return of([] as FacilityBillingRateModel[]);
            })
        );
    }

    formatDateString(date: string | Date): string {
        const localDate = new Date(date);
        return formatDate(localDate, 'MM/dd/yyyy h:mm a', this._locale);
    }

    public handleHolidayRules(holidayRules: FacilityHolidayRules): void {
        if (holidayRules) {
            this.holidayRulesCount = holidayRules.holidayRules.length;
            this.localHolidayRules = [];
            this.holidayType = this.holidayRulesCount === 0 ? 'Custom Holidays' : 'Aya Holidays';

            const usedHolidayIds = [];

            const defaultHolidayIds = [];
            if (holidayRules.defaultRule) {
                holidayRules.defaultRule.holidays.forEach((x) => defaultHolidayIds.push(x.id));
            }

            if (holidayRules.holidayRules) {
                holidayRules.holidayRules.forEach((holidayRule) => {
                    holidayRule.holidays.forEach((x) => {
                        usedHolidayIds.push(x.id);
                        this.localHolidayRules.push({
                            date: x.date,
                            name: x.name,
                            rule: this.getHolidayRule(holidayRule)
                        });
                    });
                });

                const usedHolidayIdsStatement = usedHolidayIds.sort(this.compareHoliday).toString();
                const defaultHolidayIdsStatement = defaultHolidayIds.sort(this.compareHoliday).toString();

                if (
                    this.holidayRulesCount === 1 &&
                    holidayRules.defaultRule.endRule === holidayRules.holidayRules[0].endRule &&
                    holidayRules.defaultRule.endRuleTime === holidayRules.holidayRules[0].endRuleTime &&
                    holidayRules.defaultRule.startRule === holidayRules.holidayRules[0].startRule &&
                    holidayRules.defaultRule.startRuleTime === holidayRules.holidayRules[0].startRuleTime &&
                    holidayRules.defaultRule.hoursRule === holidayRules.holidayRules[0].hoursRule &&
                    usedHolidayIdsStatement === defaultHolidayIdsStatement
                ) {
                    this.holidayType = 'Custom Holidays';
                } else {
                    this.holidayType = 'Aya Holidays';
                }
            }
        }
    }

    public enrichBillingRateGridItems(shifts: FacilityBillingShiftDifferential[]): BillingRateGridItem[] {
        const result = [];
        const dayShift = shifts.find((x) => x.day);
        if (dayShift) {
            result.push({
                shiftType: 'Day',
                allHours: dayShift.allHours,
                hours8: dayShift.hours8,
                hours10: dayShift.hours10,
                hours12: dayShift.hours12
            });
        }

        const midShift = shifts.find((x) => x.mid);
        if (midShift) {
            result.push({
                shiftType: 'Mid',
                allHours: midShift.allHours,
                hours8: midShift.hours8,
                hours10: midShift.hours10,
                hours12: midShift.hours12
            });
        }

        const nightShift = shifts.find((x) => x.night);
        if (nightShift) {
            result.push({
                shiftType: 'Night',
                allHours: nightShift.allHours,
                hours8: nightShift.hours8,
                hours10: nightShift.hours10,
                hours12: nightShift.hours12
            });
        }

        const weekendDayShift = shifts.find((x) => x.weekendDay);
        if (weekendDayShift) {
            result.push({
                shiftType: 'Weekend Day',
                allHours: weekendDayShift.allHours,
                hours8: weekendDayShift.hours8,
                hours10: weekendDayShift.hours10,
                hours12: weekendDayShift.hours12
            });
        }

        const weekendMidShift = shifts.find((x) => x.weekendMid);
        if (weekendMidShift) {
            result.push({
                shiftType: 'Weekend Mid',
                allHours: weekendMidShift.allHours,
                hours8: weekendMidShift.hours8,
                hours10: weekendMidShift.hours10,
                hours12: weekendMidShift.hours12
            });
        }

        const weekendNightShift = shifts.find((x) => x.weekendNight);
        if (weekendNightShift) {
            result.push({
                shiftType: 'Weekend Night',
                allHours: weekendNightShift.allHours,
                hours8: weekendNightShift.hours8,
                hours10: weekendNightShift.hours10,
                hours12: weekendNightShift.hours12
            });
        }

        return result;
    }

    public getHolidayRule(rule: FacilityHolidayRule): string {
        let result = '';
        if (rule.hoursRule === 'majhours') {
            result += 'Majority Hours ';
        }
        if (rule.hoursRule === 'starts') {
            result += 'Starts Within ';
        }
        if (rule.hoursRule === 'ends') {
            result += 'Ends Within ';
        }
        if (rule.hoursRule === 'falls') {
            result += 'Falls Within ';
        }
        if (rule.startRuleTime) {
            result += this.convertToStandardTime(rule.startRuleTime);
        }
        if (rule.startRule === 'daybefore') {
            result += ' Day Before ';
        }
        if (rule.startRule === 'dayof') {
            result += ' Day Of ';
        }
        result += 'to ';
        if (rule.endRuleTime) {
            result += this.convertToStandardTime(rule.endRuleTime);
        }
        if (rule.endRule === 'dayof') {
            result += ' Day Of';
        }
        if (rule.endRule === 'dayafter') {
            result += ' Day After';
        }
        return result;
    }

    private convertToStandardTime(militaryTime: string): string {
        const parsed = militaryTime.split(':');
        if (parsed.length !== 2) {
            return militaryTime;
        }
        const hour = Number(parsed[0]);
        const min = parsed[1];
        const suffix = hour >= 12 ? 'PM' : 'AM';
        const newHour = ((hour + 11) % 12) + 1;
        return `${newHour}:${min} ${suffix}`;
    }

    private compareHoliday(h1: number, h2: number): number {
        return h1 - h2;
    }

    showLoading(value: boolean): void {
        this.loadingEvent.emit(value);
    }

    formatOrintationHours(hours?: number, rate?: number, type = ''): string {
        const nonBillable = ' Non-Billable';

        let result = `${hours} hours`;
        result += rate && rate > 0 ? (type.trim() === '$' ? ` at ${type}${rate}` : ` at ${rate}${type}`) : nonBillable;

        return result;
    }
}
